import React, { useEffect, useReducer, useRef, useState } from 'react'

import { SingleClickButton } from 'components/instant-win/SingleClickButton'
import InstantWinModal from 'components/instant-win/InstantWinModal'
import { IncommOfferWinModal } from 'components/instant-win/IncommOfferWinModal'
import { getUserType } from 'global/context/sub-contexts/userContext'
import analytics from 'global/AnalyticsData'

import { DialogMessage } from 'components/instant-win/DialogMessage'

import { userTypeEnum } from 'services/user'

const instantWinReducer = (state, action) => {
  // always include current action.type in state. allows useEffect to support loading data.
  // anti-pattern? greatest pattern?
  state.currentAction = action.type
  switch (action.type) {
    case 'LOADED_DATA':
      return {
        ...state,
        isReady: true,
        isError: false,
        instantWinConfig: action.instantWinConfig,
        data: action.data,
        currentAction: 'LOADED_DATA',
      }
    // this break is not configured w/ instant win
    case 'NO_INSTANT_WIN':
      return {
        ...state,
        isReady: true,
      }
    case 'QUALIFIED_EVENT':
      return {
        ...state,
        isModalOpen: true,
        context: action.context,
        data: action.data,
        token: action.token,
        wheelConfig: action.wheelConfig,
        wheelTheme: action.wheelTheme,
        button: action.button,
        prizePool: action.prizePool,
        hasSecondTry: action.hasSecondTry,
        secondTryStepId: action.secondTryStepId,
      }
    // BR-5696: a No Graphic pool opens its modal with a spinner while qualify and entry run
    case 'NO_GRAPHIC_LOADING':
      return {
        ...state,
        isModalOpen: true,
        context: action.context,
        prizePool: action.prizePool,
        wheelConfig: {
          noGraphic: true,
          loading: true,
          keepOpen: true,
          hideForfeit: true,
          shouldSpin: false,
        },
        button: null,
      }
    case 'UNQUALIFIED_EVENT':
      return {
        ...state,
        isModalOpen: false,
        token: null,
      }

    case 'UNQUALIFIED_EVENT_WITH_MESSAGE':
      return {
        ...state,
        isModalOpen: true,
        errorMessage: action.message,
        errorTitle: action.title || null,
        token: null,
      }
    // allows us to send wheel info down to modal. may not need separate WINNER/LOSER
    case 'ENTRY_RESULT_WINNER':
      return {
        ...state,
        isWinner: action.isWinner,
        hasSecondTry: action.hasSecondTry,
        wheelConfig: action.wheelConfig,
        handleAfterSpin: action.handleAfterSpin,
        prize: action.prize,
      }

    case 'ENTRY_RESULT_LOSER':
      return {
        ...state,
        isWinner: action.isWinner,
        hasLostOnce: action.hasLostOnce,
        hasLostTwice: action.hasLostTwice,
        wheelConfig: action.wheelConfig,
        handleAfterSpin: action.handleAfterSpin,
        prize: null,
      }

    // SECOND_ENTRY_RESULT_WINNER & SECOND_ENTRY_RESULT_LOSER
    // are very similar to ENTRY_RESULT_WINNER & ENTRY_RESULT_LOSER
    // duplicating felt like it would be more clear
    case 'SECOND_ENTRY_RESULT_WINNER':
      return {
        ...state,
        isWinner: action.isWinner,
        hasSecondTry: action.hasSecondTry,
        wheelConfig: action.wheelConfig,
        handleAfterSpin: action.handleAfterSpin,
        prize: action.prize,
      }

    case 'SECOND_ENTRY_RESULT_LOSER':
      return {
        ...state,
        isWinner: action.isWinner,
        hasLostOnce: action.hasLostOnce,
        hasLostTwice: action.hasLostTwice,
        wheelConfig: action.wheelConfig,
        handleAfterSpin: action.handleAfterSpin,
        prize: null,
      }

    // triggered after wheel is done spinning
    // we show updated copy
    case 'DISPLAY_RESULT':
      return {
        ...state,
        wheelConfig: action.wheelConfig,
        button: action.button,
      }
    case 'QUALIFIED_SECOND_CHANCE':
      return {
        ...state,
        wheelConfig: action.wheelConfig,
        wheelTheme: action.wheelTheme,
        button: action.button,
        isModalOpen: true,
        token: action.token,
      }
    case 'CLOSE_MODAL':
      return {
        ...state,
        isModalOpen: false,
      }

    default:
      console.warn(`unhandled action.type: ${action.type}`)
    // throw new Error()
  }
}

/**
    this handles the mess of looking up whether an event is applicable for us
    it transforms a step-specific payload into a common format

    @param eventContext comes from the source event that sent this event

    @returns
    {
        payload                 // reshaped eventContext based on eventName
        prizePoolId             // determined
    }

    note: not inside the controller. pass params for what you need.
    

*/
const isValidEvent = (
  eventName,
  eventContext,
  instantWinConfig = { events: [] },
  permalink,
  isSecondChance,
) => {
  if (!instantWinConfig) {
    return
  }

  if (isNaN(instantWinConfig.id)) {
    return
  }

  const eventsWithPermalinks = instantWinConfig.events.filter(
    instantWinEvent => {
      return instantWinEvent.permalink && instantWinEvent.permalink !== ''
    },
  )

  const eventsWithoutPermalinks = instantWinConfig.events.filter(
    instantWinEvent => {
      return !instantWinEvent.permalink || instantWinEvent.permalink === ''
    },
  )

  // first look for a permalink-specific match. These are more specific so should be checked first.
  let configuredEvent = eventsWithPermalinks.find(instantWinEvent => {
    return (
      instantWinEvent.eventName === eventName &&
      instantWinEvent.isSecondTry === isSecondChance &&
      instantWinEvent.permalink === permalink
    )
  })

  // couldn't find one? now try looking for one that matches that has no permalink
  if (!configuredEvent) {
    configuredEvent = eventsWithoutPermalinks.find(instantWinEvent => {
      return (
        instantWinEvent.eventName === eventName &&
        instantWinEvent.isSecondTry === isSecondChance
      )
    })
  }

  if (configuredEvent) {
    // different events have different payloads, depending on thei step. we need to map their
    // payload format to a common one for instant win

    if (configuredEvent.eventName === 'leadgen_submit') {
      // We pass along the leadgen form data along with the qualifying event.
      // The leadgen form comes in to us in `eventContext.form`.
      // to be safe, remove unused fields aka sanitizeEmptyValues
      let payload = {}
      Object.keys(eventContext.form).forEach(key => {
        if (eventContext.form[key] == null || eventContext.form[key] === '') {
          // skip this empty key
        } else {
          payload[key] = eventContext.form[key]
        }
      })

      payload.eventName = eventName
      payload.stepId = eventContext.stepId
      payload.instantWinId = Number(instantWinConfig.id)
      payload.leadGenSubmissionToken = payload.id
      delete payload.token

      return {
        payload: payload,
        prizePoolId: configuredEvent.prizePoolId,
      }
    }

    // BR-5695: the Beaker2 Form step. The API checks formSubmissionId belongs to this user
    // and has not qualified before; the contact details are stored on the qualifying event.
    if (configuredEvent.eventName === 'form_submit') {
      const contact = eventContext.contact || {}
      let payload = {}
      ;['email', 'firstName', 'lastName', 'phoneNumber'].forEach(key => {
        const value = contact[key] ?? eventContext.form?.[key]
        if (value != null && value !== '') {
          payload[key] = value
        }
      })

      payload.eventName = eventName
      payload.stepId = eventContext.stepId
      payload.instantWinId = Number(instantWinConfig.id)
      payload.formSubmissionId = eventContext.form?.id

      return {
        payload: payload,
        prizePoolId: configuredEvent.prizePoolId,
      }
    }

    if (configuredEvent.eventName === 'receipt-submission-status-approved') {
      let payload = eventContext.receiptStatusResult
      payload.eventName = eventName
      payload.stepId = eventContext.stepId
      payload.instantWinId = Number(instantWinConfig.id)
      payload.receiptSubmissionToken = payload.token
      delete payload.token

      return {
        payload: payload,
        prizePoolId: configuredEvent.prizePoolId,
      }
    }

    // share gives us basically no info. It's really only useful as a second try.
    if (configuredEvent.eventName === 'share_click') {
      console.log('INSTANT__WIN_SHARE_CLICK')
      analytics.stepEvent('instant_win_share_click', eventContext, {
        result_id: instantWinConfig.id,
        result: 'share_click',
      })
      return {
        payload: {},
        prizePoolId: configuredEvent.prizePoolId
          ? configuredEvent.prizePoolId
          : null,
      }
    }
  }

  return null
}

const isFormEvent = eventName =>
  eventName === 'leadgen_submit' || eventName === 'form_submit'

const InstantWinController = ({
  render,
  brk,
  permalink,
  instantWinId,
  userType,
  getInstantWin,
  qualifyingEvent,
  createEntry,
  getWheelConfig,
}) => {
  const isGuest = getUserType() === userTypeEnum.GUEST
  const [incommWinModalOpen, setIncommWinModalOpen] = useState(false)
  const [incommOfferId, setIncommOfferId] = useState(null)
  const [incommPrizeDisplay, setIncommPrizeDisplay] = useState({})
  // this click handler is defined first so we can use it in <Button onClick /> in the useReducer definition
  // rename it to handleStartSpin
  const handleModalButtonClick = async context => {
    console.log(`handleModalButtonClick`)

    // let's see if we won!
    const response = await createEntry(
      stateRef.current.instantWinId,
      stateRef.current.token,
      //   stateRef.current.email,
    )
    const data = await response.json()
    console.log(`response`, data)

    if (data.winner === true) {
      const prize = stateRef.current.prizePool.prizes.find((prize, i) => {
        return prize.id === data.prizeId
      })

      analytics.stepEvent('instant_win_result_won', context, {
        isGuest: isGuest,
        secondChance: false,
        result_id: instantWinId,
        result: 'won',
      })

      dispatch({
        type: 'ENTRY_RESULT_WINNER',
        isWinner: true,
        wheelConfig: Object.assign({}, stateRef.current.wheelConfig, {
          shouldSpin: true,
          prizeId: data.prizeId,
          hideForfeit: true,
        }),
        handleAfterSpin: handleAfterSpin,
        prize: prize,
      })
    } else {
      // Find a no-win slice (id === 0) or fall back to the first slice
      const loserPrize = stateRef.current.data.find(element => element.id === 0)
        || stateRef.current.data[0]

      analytics.stepEvent('instant_win_result_lost', context, {
        isGuest: isGuest,
        result_id: instantWinId,
        result: 'lost',
      })

      dispatch({
        type: 'ENTRY_RESULT_LOSER',
        isWinner: false,
        hasLostOnce: true,
        wheelConfig: Object.assign({}, stateRef.current.wheelConfig, {
          shouldSpin: true,
          prizeId: loserPrize.id,
          hideForfeit: true,
        }),
        handleAfterSpin: handleAfterSpin,
      })
    }
  }

  // this almost duplicates handleModalButtonClick
  const handleSecondChanceButtonClick = async (context = {}) => {
    console.log('handleSecondChanceButtonClick')

    // let's see if we won!
    const response = await createEntry(
      stateRef.current.instantWinId,
      stateRef.current.token,
      stateRef.current.email,
    )
    const data = await response.json()

    // more hard-coded logic here
    if (data.isWinner === true) {
      const prize = stateRef.current.prizePool.prizes.find((prize, i) => {
        return prize.id === data.prizeId
      })

      analytics.stepEvent('instant_win_result_won', context, {
        isGuest: isGuest,
        secondChance: true,
        result_id: instantWinId,
        result: 'won',
      })

      dispatch({
        type: 'SECOND_ENTRY_RESULT_WINNER',
        isWinner: true,
        hasSecondTry: false,
        hasLostTwice: false,
        wheelConfig: Object.assign({}, stateRef.current.wheelConfig, {
          shouldSpin: true,
          prizeId: data.prizeId,
          hideForfeit: true,
        }),
        handleAfterSpin: handleAfterSpinSecondTry,
        prize: prize,
      })
    } else {
      // Find a no-win slice (id === 0) or fall back to the first slice
      const loserPrize = stateRef.current.data.find(element => element.id == 0)
        || stateRef.current.data[0]

      analytics.stepEvent('instant_win_result_lost', context, {
        isGuest: isGuest,
        secondChance: true,
        result_id: instantWinId,
        result: 'lost',
      })

      dispatch({
        type: 'SECOND_ENTRY_RESULT_LOSER',
        isWinner: false,
        hasLostOnce: true,
        hasLostTwice: true,
        wheelConfig: Object.assign({}, stateRef.current.wheelConfig, {
          shouldSpin: true,
          prizeId: loserPrize.id,
          hideForfeit: true,
        }),
        handleAfterSpin: handleAfterSpinSecondTry,
        prize: loserPrize,
      })
    }
  }
  /*
        the data sent to configure wheel items takes the form
        const data = [
            { id: 0, option: 'Sorry' },
            { id: 1, option: '$500' },
            { id: 2, option: '$100' },
            { id: 3, option: '$10' },
            { id: 4, option: 'Sorry' },
            { id: 5, option: '$500' },
            { id: 6, option: '$100' },
            { id: 7, option: '$10' },
            { id: 8, option: 'Sorry' }
        ]

    */
  const [state, dispatch] = useReducer(instantWinReducer, {
    currentAction: 'INIT',
    brk: brk,
    context: { instantWinId: instantWinId, permalink: permalink },
    permalink: permalink,
    instantWinId: instantWinId,
    isReady: false,
    isError: false,
    isModalOpen: false,
    instantWinConfig: null,
    prizePool: null,
    hasSecondTry: false,
    secondTryStepId: null,
    hasLostOnce: false,
    hasLostTwice: false,
    secondChanceUsed: false,
    prizeNumber: 0,
    button: null,
    wheelConfig: { shouldSpin: false, data: [], hideForfeit: false },
    wheelTheme: null,
    errorMessage: null,
  })

  // storing our state in a useRef() ?
  // was getting an odd behavior where event handlers were running and seeing old state
  // https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback
  // so we throw state in a ref. feels gross.
  const stateRef = useRef()
  stateRef.current = state

  useEffect(() => {
    const asyncWrapper = async () => {
      // only kick things off after we have a user token in place
      if (instantWinId != null && userType && userType != userTypeEnum.NONE) {
        const result = await getInstantWin(instantWinId)

        // we can't determine prizes without a prize pool specified, which would happen when an event occurs
        dispatch({
          type: 'LOADED_DATA',
          instantWinConfig: result,
        })
      } else {
        dispatch({
          type: 'NO_INSTANT_WIN',
        })
      }
    }

    // don't run our initialization code on builds, do it at runtime
    if (typeof window != 'undefined') {
      asyncWrapper()
    }
  }, [instantWinId, userType])

  // incoming instant win events
  const handleEvent = async ({ eventName, context = {} }) => {
    console.log(`instant win event received: ${eventName}`, context)
    if (stateRef.current.instantWinId === null) {
      return
    }

    const isSecondTry =
      stateRef.current.hasSecondTry == true &&
      stateRef.current.hasLostOnce === true &&
      stateRef.current.secondChanceUsed === false

    const hasUsedUpTries =
      stateRef.current.hasSecondTry == true &&
      stateRef.current.hasLostOnce === true &&
      stateRef.current.secondChanceUsed === true
    if (hasUsedUpTries) {
      analytics.stepEvent('instant_win_invalid_qualifying_event', context, {
        isGuest: isGuest,
      })
    }
    //todo: a little confusing since it returns an object, not a boolean
    const validEvent = isValidEvent(
      eventName,
      context,
      stateRef.current.instantWinConfig,
      stateRef.current.permalink,
      isSecondTry,
    )

    console.log(`TEST`, validEvent)

    if (validEvent) {
      analytics.stepEvent('instant_win_qualified_event_accepted', context, {
        isGuest: isGuest,
      })
      // overlay whatever is new on top of whatever we had
      // needed for events like video_share that give us no data
      const newContext = Object.assign(
        {},
        stateRef.current.context,
        { eventName: eventName },
        validEvent.payload,
      )

      // either our event mapping has a poolId or we fall back on whatever we had
      let prizePoolId = stateRef.current.prizePool
        ? stateRef.current.prizePool.id
        : null
      if (validEvent.prizePoolId) {
        prizePoolId = validEvent.prizePoolId
      }

      // we didn't get a prize pool from the event - if we only have one defined, use it
      if (!prizePoolId) {
        try {
          if (
            stateRef.current.instantWinConfig.prizePools &&
            stateRef.current.instantWinConfig.prizePools.length == 1
          ) {
            prizePoolId = stateRef.current.instantWinConfig.prizePools[0].id
          }
        } catch (e) {
          console.log(e)
        }
      }

      if (!prizePoolId) {
        console.warn('Invalid/missing prize pool')
        return
      }

      newContext['instantWinPrizePoolId'] = prizePoolId

      // API CALL to see if this was a valid qualifying event
      // BR-5696: no wheel to wait for, so show the modal now; otherwise the user sees
      // nothing while qualify and entry run.
      const pendingPool = stateRef.current.instantWinConfig.prizePools?.find(
        element => element.id == prizePoolId,
      )
      const showsLoading = pendingPool?.displayType === 'NO_GRAPHIC' && !isSecondTry
      const failLoading = () =>
        showsLoading &&
        dispatch({
          type: 'UNQUALIFIED_EVENT_WITH_MESSAGE',
          message: 'Something went wrong. Please try again later.',
        })
      if (showsLoading) {
        dispatch({ type: 'NO_GRAPHIC_LOADING', prizePool: pendingPool, context: newContext })
      }

      let qualifyingEventResult
      try {
        const response = await qualifyingEvent(newContext) // validEvent.payload
        // BR-5696: an API error is JSON too, so without this a 500 would read as "not eligible".
        // Wheel pools keep their current handling.
        if (showsLoading && !response.ok) {
          throw new Error(`qualify returned ${response.status}`)
        }
        qualifyingEventResult = await response.json()
      } catch (e) {
        console.warn('instant win: qualify failed', e)
        failLoading()
        return
      }
      console.log(`qualifyingEventResult`, qualifyingEventResult)
      try {
        console.log(
          `qualifyingEventResult.eventStatus`,
          qualifyingEventResult.eventStatus,
        )
        if (qualifyingEventResult.eventStatus === 'SUCCESSFUL') {
          console.log(`response`, qualifyingEventResult)

          // lookup the wheel data asspciated with the receipt step reward they chose
          const prizePool = stateRef.current.instantWinConfig.prizePools.find(
            element => {
              return element.id == prizePoolId
            },
          )

          if (!prizePool) {
            console.warn(
              "Can't find prizepool for ",
              qualifyingEventResult.rewardId,
            )
            return
          }
          // BR-5696: no wheel. Enter straight away and show the result in one modal.
          if (prizePool.displayType === 'NO_GRAPHIC' && !isSecondTry) {
            analytics.stepEvent('instant_win_qualify_event_successful', context, {
              result: 'successful',
              resultId: instantWinId,
            })
            await showNoGraphicResult({
              prizePool,
              token: qualifyingEventResult.token,
              context: newContext,
            })
            return
          }

          // Fetch wheel config from separate endpoint if available
          let fetchedWheelConfig = null
          if (getWheelConfig) {
            fetchedWheelConfig = await getWheelConfig(prizePool.id)
          }

          // format wheel data
          let wheelData
          let wheelTheme = null

          const backendWheelConfig = fetchedWheelConfig
          if (backendWheelConfig && backendWheelConfig.slices && backendWheelConfig.slices.length > 0) {
            // Backend-configured wheel: use slices for segments and theme for colors
            const sortedSlices = [...backendWheelConfig.slices].sort((a, b) => a.sortOrder - b.sortOrder)
            wheelData = sortedSlices.map(slice => ({
              id: slice.prizeId ?? 0,
              option: slice.label,
              style: {
                backgroundColor: slice.backgroundColor,
                textColor: slice.textColor,
                fontSize: slice.fontSize,
              },
            }))
            wheelTheme = {
              outerWheelBorderColor: backendWheelConfig.outerWheelBorderColor,
              innerWheelBorderColor: backendWheelConfig.innerWheelBorderColor,
              tickerBorderColor: backendWheelConfig.tickerBorderColor,
              tickerColor: backendWheelConfig.tickerColor,
              iconBorderColor: backendWheelConfig.iconBorderColor,
              iconImageUrl: backendWheelConfig.iconImageUrl,
              textOrientation: backendWheelConfig.textOrientation,
              sliceBorderColor: backendWheelConfig.sliceBorderColor,
              sliceBorderWidth: backendWheelConfig.sliceBorderWidth,
            }
          } else {
            // Legacy: build from prizes
            wheelData = prizePool.prizes.map((item, i) => {
              return { id: item.id, option: item.title }
            })
            wheelData.unshift({ id: 0, option: 'Sorry!' }) // unshift to make this prize 0
          }

          // BR-5696: drop what a No Graphic result left in state, or this wheel would render without its wheel
          const { noGraphic, imageUrl, loading, keepOpen, ...previousWheelConfig } =
            stateRef.current.wheelConfig
          let wheelConfig = noGraphic
            ? { ...previousWheelConfig, hideForfeit: false }
            : stateRef.current.wheelConfig
          wheelConfig.title = prizePool.modalLaunchTitle
          wheelConfig.bodyCopy = prizePool.modalLaunchBodyCopy
          wheelConfig.footerCopy = prizePool.sharedFooterCopy
          wheelConfig.buttonCopy = prizePool.modalLaunchButtonCopy

          console.log('isSecondTry', isSecondTry)

          if (isSecondTry) {
            let button = (
              <SingleClickButton
                label="Try again!"
                onClick={handleSecondChanceButtonClick}
                className="instantWinButton"
                key={1}
              />
            )
            dispatch({
              type: 'QUALIFIED_SECOND_CHANCE', // OPEN_MODAL
              token: qualifyingEventResult.token,
              context: newContext,
              wheelConfig: wheelConfig,
              wheelTheme: wheelTheme,
              button: button,
            })
          } else {
            let button = (
              <SingleClickButton
                label={prizePool.modalLaunchButtonCopy}
                onClick={handleModalButtonClick}
                className="instantWinButton"
                key={2}
              />
            )

            // If we get here, we have a valid qualifying event and we can spin the wheel
            // So we can trigger the analytics event for leadgen_submit
            console.log('INSTANT__WIN_EVENT_SUCCESSFUL')
            analytics.stepEvent(
              'instant_win_qualify_event_successful',
              context,
              {
                result: 'successful',
                resultId: instantWinId,
              },
            )

            dispatch({
              type: 'QUALIFIED_EVENT', // OPEN_MODAL
              token: qualifyingEventResult.token,
              data: wheelData,
              prizePool: prizePool,
              context: newContext,
              wheelConfig: wheelConfig,
              wheelTheme: wheelTheme,
              button: button,
              hasSecondTry: prizePool.hasSecondTry,
              secondTryStepId: prizePool.secondTryStepId,
            })
          }
        } else {
          console.log('Invalid qualifying event', qualifyingEventResult)
          // BR-5696: the pool's own copy wins over the default sweepstakes messages, and with it
          // the pool's lose title replaces "Thanks for playing!"
          const notEligiblePool = stateRef.current.instantWinConfig.prizePools?.find(
            element => element.id == prizePoolId,
          )
          const notEligibleCopy = firstNonBlank(notEligiblePool?.modalNotEligibleCopy)
          dispatch({
            type: 'UNQUALIFIED_EVENT_WITH_MESSAGE', // NO SPIN MODAL
            token: qualifyingEventResult.token,
            title: notEligibleCopy ? firstNonBlank(notEligiblePool.modalLoseTitle) : null,
            message:
              notEligibleCopy ||
              qualifyingEventResult.message ||
              "Though you didn't win this time, you have earned a sweepstakes entry for the Grand Prize! Come back each day to earn additional sweepstakes entries. See Official Rules for reference.",
            context: newContext,
          })
        }
      } catch (e) {
        // generally catches our own data issues
        console.warn(e)
        failLoading()
      }

      return
    } else {
      analytics.stepEvent('instant_win_qualified_event_rejected', context, {
        isGuest: isGuest,
      })
    }
  }

  const handleCloseModal = () => {
    analytics.stepEvent(
      'instant_win_modal_close',
      {},
      {
        isGuest: isGuest,
        result_id: instantWinId,
        result: 'close',
      },
    )

    dispatch({
      type: 'CLOSE_MODAL',
    })
  }

  const getPrizeButtonCopy = (prize, fallback) => {
    const buttonCopy = prize?.winnerModalButtonCopy
    return typeof buttonCopy === 'string' && buttonCopy.trim() !== ''
      ? buttonCopy
      : fallback
  }

  const openIncommPrizeModal = prize => {
    setIncommOfferId(prize.incommOfferId)
    setIncommPrizeDisplay({
      offerTitle: prize.metadata?.offerTitle || null,
      offerDescription: prize.metadata?.offerDescription || null,
      bannerImageUrl: prize.metadata?.bannerImageUrl || null,
      availableStoreIdsOverride: prize.metadata?.availableStoreIdsOverride || null,
      ctaText: prize.metadata?.ctaText || null,
    })
    setIncommWinModalOpen(true)
    // Close the parent "Congrats!" modal so only the Incomm flow is visible.
    // Skip close analytics: the user is progressing, not abandoning.
    dispatch({ type: 'CLOSE_MODAL' })
  }

  const getWinnerButton = (prize, key, labelOverride) => {
    if (prize?.prizeType === 'INCOMM_OFFER') {
      return (
        <SingleClickButton
          label={labelOverride || getPrizeButtonCopy(prize, 'Claim Offer')}
          onClick={() => openIncommPrizeModal(prize)}
          className="instantWinButton"
          key={key}
        />
      )
    }

    return (
      <SingleClickButton
        label={labelOverride || getPrizeButtonCopy(prize, 'Close')}
        onClick={handleCloseModal}
        className="instantWinButton"
        key={key}
      />
    )
  }

  const firstNonBlank = (...values) =>
    values.find(value => typeof value === 'string' && value.trim() !== '') || ''

  // BR-5696: a NO_GRAPHIC pool skips the launch modal and the wheel. The entry is created
  // right away and one modal shows the pool's copy (falling back to the prize's winner copy),
  // whose CTA claims the prize (opens the InComm offer for an InComm prize).
  const showNoGraphicResult = async ({ prizePool, token, context }) => {
    let data
    try {
      const response = await createEntry(stateRef.current.instantWinId, token)
      if (!response.ok) {
        throw new Error(`entry returned ${response.status}`)
      }
      data = await response.json()
    } catch (e) {
      console.warn('instant win: entry failed', e)
      dispatch({
        type: 'UNQUALIFIED_EVENT_WITH_MESSAGE',
        message: 'Something went wrong. Please try again later.',
      })
      return
    }

    const prize =
      data?.winner === true
        ? prizePool.prizes.find(item => item.id === data.prizeId)
        : null

    const wheelConfig = {
      noGraphic: true,
      hideForfeit: true,
      shouldSpin: false,
      footerCopy: prizePool.sharedFooterCopy,
      imageUrl: prizePool.noGraphicImageUrl || null,
    }
    let button

    if (prize) {
      analytics.stepEvent('instant_win_result_won', context, {
        isGuest: isGuest,
        secondChance: false,
        result_id: instantWinId,
        result: 'won',
      })
      // the prize is already awarded, so only the CTA closes the modal (no Escape or backdrop)
      wheelConfig.keepOpen = true
      wheelConfig.title = firstNonBlank(prizePool.noGraphicCopy, prize.winnerModalTitle)
      wheelConfig.bodyCopy = firstNonBlank(
        prizePool.noGraphicSubcopy,
        prize.winnerModalBodyCopy,
      )
      button = getWinnerButton(prize, 7, firstNonBlank(prizePool.noGraphicCtaCopy))
    } else {
      analytics.stepEvent('instant_win_result_lost', context, {
        isGuest: isGuest,
        result_id: instantWinId,
        result: 'lost',
      })
      wheelConfig.title = prizePool.modalLoseTitle
      wheelConfig.bodyCopy = prizePool.modalLoseBodyCopy
      button = (
        <SingleClickButton
          label={firstNonBlank(prizePool.modalLoseButtonCopy, 'Close')}
          onClick={handleCloseModal}
          className="instantWinButton"
          key={8}
        />
      )
    }

    dispatch({
      type: 'QUALIFIED_EVENT',
      token: token,
      data: [],
      prizePool: prizePool,
      context: context,
      wheelConfig: wheelConfig,
      wheelTheme: null,
      button: button,
      hasSecondTry: false,
      secondTryStepId: null,
    })
  }

  // Wheel component will call this after spinning is complete
  // this allows us to update copy based on win/lose
  const handleAfterSpin = async () => {
    console.log('AFTER SPIN')
    // update winner / loser copy

    let wheelConfig = stateRef.current.wheelConfig
    wheelConfig.shouldSpin = false
    let button = null

    if (stateRef.current.isWinner && stateRef.current.prize) {
      wheelConfig.title = stateRef.current.prize.winnerModalTitle
      wheelConfig.bodyCopy = stateRef.current.prize.winnerModalBodyCopy

      button = getWinnerButton(stateRef.current.prize, 3)
      dispatch({
        type: 'DISPLAY_RESULT',
        wheelConfig: wheelConfig,
        button: button,
      })
    } else {
      // when a pool has a second chance lose copy is second try copy. a concept that is confusing.
      wheelConfig.title = stateRef.current.prizePool.modalLoseTitle
      wheelConfig.bodyCopy = stateRef.current.prizePool.modalLoseBodyCopy

      if (stateRef.current.prizePool.hasSecondTry) {
        handleEvent({ eventName: 'share_click' })
      } else {
        button = (
          <SingleClickButton
            label={stateRef.current.prizePool.modalLoseButtonCopy}
            onClick={handleCloseModal}
            className="instantWinButton"
            key={6}
          />
        )
        dispatch({
          type: 'DISPLAY_RESULT',
          wheelConfig: wheelConfig,
          button: button,
        })
      }
    }
  }

  // Wheel component will call this after spinning is complete
  // this allows us to update copy based on win/lose
  const handleAfterSpinSecondTry = async () => {
    console.log('AFTER SPIN 2ND TRY')
    // update winner / loser copy

    let wheelConfig = stateRef.current.wheelConfig
    wheelConfig.shouldSpin = false
    let button = null

    if (stateRef.current.isWinner && stateRef.current.prize) {
      wheelConfig.title = stateRef.current.prize.winnerModalTitle
      wheelConfig.bodyCopy = stateRef.current.prize.winnerModalBodyCopy

      button = getWinnerButton(stateRef.current.prize, 4)
    } else {
      wheelConfig.title = stateRef.current.prizePool.modalLose2Title
      wheelConfig.bodyCopy = stateRef.current.prizePool.modalLose2BodyCopy

      button = (
        <SingleClickButton
          label="Close"
          onClick={handleCloseModal}
          className="instantWinButton"
          key={5}
        />
      ) // key to force a remount
    }

    dispatch({
      type: 'DISPLAY_RESULT',
      wheelConfig: wheelConfig,
      button: button,
    })
  }

  let instantWinModal = null
  // console.log(stateRef.current.wheelConfig)
  if (typeof window != 'undefined') {
    if (stateRef.current.errorMessage) {
      instantWinModal = (
        <DialogMessage
          title={stateRef.current.errorTitle || 'Thanks for playing!'}
          message={stateRef.current.errorMessage}
        />
      )
    } else {
      instantWinModal = (
        <InstantWinModal
          wheelConfig={stateRef.current.wheelConfig}
          wheelTheme={stateRef.current.wheelTheme}
          data={stateRef.current.data}
          isModalOpen={stateRef.current.isModalOpen}
          handleCloseModal={handleCloseModal}
          button={stateRef.current.button}
          handleAfterSpin={stateRef.current.handleAfterSpin}
          className={`${`break-permalink-${permalink}`} f`}
        />
      )
    }
  }

  // we only want the eventHandler to be made available once we're ready.
  if (stateRef.current.instantWinConfig) {
    return (
      <>
        {render(stateRef.current.isReady, instantWinModal, handleEvent)}
        <IncommOfferWinModal
          open={incommWinModalOpen}
          onClose={() => setIncommWinModalOpen(false)}
          incommOfferId={incommOfferId}
          offerTitle={incommPrizeDisplay.offerTitle}
          offerDescription={incommPrizeDisplay.offerDescription}
          bannerImageUrl={incommPrizeDisplay.bannerImageUrl}
          availableStoreIdsOverride={incommPrizeDisplay.availableStoreIdsOverride}
          ctaText={incommPrizeDisplay.ctaText}
          prefilledEmail={
            // When the qualifying event was a leadgen or form step submission, the
            // form values (incl. email) are merged into context — reuse to prefill the
            // Incomm form so the user doesn't have to re-enter their email.
            isFormEvent(stateRef.current.context?.eventName)
              ? stateRef.current.context?.email || null
              : null
          }
          prefilledContact={
            // BR-5706: the InComm form only asks for email; the name the user already
            // gave greets them in the verification email.
            isFormEvent(stateRef.current.context?.eventName)
              ? {
                  firstName: stateRef.current.context?.firstName || null,
                  lastName: stateRef.current.context?.lastName || null,
                }
              : null
          }
        />
      </>
    )
  } else {
    return render(stateRef.current.isReady, instantWinModal, null)
  }
}

export { InstantWinController, isValidEvent }
